import { isUnknownArray } from "../types/data";
import type { ProfileUser, PrivacyMode } from "../types/profile";
import type { JsonValue } from "../types/data";
interface DirectoryAuth { currentUser: { getIdToken: () => Promise<string> } | null; }
interface DirectoryProfile extends ProfileUser { uid: string; }
interface DirectoryResponse { ok?: boolean; error?: string; code?: string; profiles?: DirectoryProfile[]; profile?: DirectoryProfile | null; }

const LOCAL_SHARE_SERVER_PORT =
  process.env.REACT_APP_SHARE_SERVER_PORT || "3002";
const PUBLIC_SITE_ORIGIN = "https://cineparagon.com";
const DIRECTORY_REQUEST_TIMEOUT_MS = 8000;

const getDirectoryApiBaseUrl = (
  location: Partial<Pick<Location, "hostname" | "port" | "origin">> | null = typeof window !== "undefined" ? window.location : null
) => {
  if (process.env.REACT_APP_SHARE_SERVER_URL) {
    return process.env.REACT_APP_SHARE_SERVER_URL.replace(/\/$/, "");
  }
  if (
    location?.hostname === "localhost" &&
    location?.port !== LOCAL_SHARE_SERVER_PORT
  ) {
    return `http://localhost:${LOCAL_SHARE_SERVER_PORT}`;
  }
  return location?.origin || "";
};

const toSharePath = (path: string) =>
  String(path).replace(/^\/api\/profile-directory(?=\/|$)/, "/share/profile-directory");

export const getProfileDirectoryRequestUrls = (
  path: string,
  location: Partial<Pick<Location, "hostname" | "port" | "origin">> | null = typeof window !== "undefined" ? window.location : null
) => {
  const baseUrl = getDirectoryApiBaseUrl(location);
  const apiPath = String(path);
  const sharePath = toSharePath(apiPath);
  const isLocalReactServer =
    location?.hostname === "localhost" &&
    location?.port !== LOCAL_SHARE_SERVER_PORT;
  const paths = isLocalReactServer ? [apiPath, sharePath] : [sharePath, apiPath];
  const urls = paths.map((requestPath) => `${baseUrl}${requestPath}`);

  // Local development can use the deployed directory if the optional server
  // on port 3002 is not running. A 404 simply means that backend has not been
  // rolled out yet and the Firestore search remains available as a fallback.
  if (isLocalReactServer && !process.env.REACT_APP_SHARE_SERVER_URL) {
    urls.push(`${PUBLIC_SITE_ORIGIN}${toSharePath(apiPath)}`);
  }
  return [...new Set(urls)];
};

const fetchWithTimeout = async (url: string|URL|Request, options: RequestInit|undefined) => {
  if (typeof AbortController === "undefined") return fetch(url, options);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), DIRECTORY_REQUEST_TIMEOUT_MS);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timeoutId);
  }
};

const requestProfileDirectory = async (auth: DirectoryAuth | null | undefined, path: string, options: { method?: string; body?: JsonValue } = {}) => {
  const token = await auth?.currentUser?.getIdToken?.();
  if (!token) {
    const error: Error & { code?: string; status?: number } = new Error("Authentication is required");
    error.code = "profile-directory/auth-required";
    throw error;
  }

  const requestOptions = {
    method: options.method || "GET",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json"
    },
    body: options.body ? JSON.stringify(options.body) : undefined
  };
  const urls = getProfileDirectoryRequestUrls(path);
  let response: Response | null = null;
  let networkError: unknown = null;
  for (let index = 0; index < urls.length; index += 1) {
    try {
      response = await fetchWithTimeout(urls[index], requestOptions);
    } catch (error) {
      networkError = error;
      response = null;
      continue;
    }
    if (response.status === 404 && index < urls.length - 1) continue;
    break;
  }

  if (!response) {
    const error: Error & { code?: string; status?: number } = new Error("Profile directory is unavailable");
    error.code = "profile-directory/network-error";
    error.cause = networkError;
    throw error;
  }
  const payload: DirectoryResponse = await response.json().catch(() => ({}));
  if (!response.ok || payload?.ok === false) {
    const error: Error & { code?: string; status?: number } = new Error(payload?.error || "Profile directory is unavailable");
    error.code = payload?.code || "profile-directory/request-failed";
    error.status = response.status;
    throw error;
  }
  return payload;
};

export const searchProfileDirectory = async ({ auth, term, limit = 12 }: { auth: DirectoryAuth | null | undefined; term: string; limit?: number }) => {
  const query = new URLSearchParams({
    q: String(term || "").trim(),
    limit: String(Math.min(12, Math.max(1, Number(limit) || 12)))
  });
  const payload = await requestProfileDirectory(
    auth,
    `/api/profile-directory/search?${query.toString()}`
  );
  return isUnknownArray(payload?.profiles) ? payload.profiles : [];
};

export const getProfileDirectoryEntry = async ({ auth, uid }: { auth: DirectoryAuth | null | undefined; uid: string }) => {
  const payload = await requestProfileDirectory(
    auth,
    `/api/profile-directory/lookup/${encodeURIComponent(String(uid || ""))}`
  );
  return payload?.profile || null;
};

export const syncProfileDirectoryEntry = async ({ auth, privacyMode }: { auth: DirectoryAuth | null | undefined; privacyMode: PrivacyMode }) => {
  const payload = await requestProfileDirectory(
    auth,
    "/api/profile-directory/sync",
    {
      method: "POST",
      body: { privacyMode }
    }
  );
  return payload?.profile || null;
};
